/**
 * Direct LeadIntake submission client (create-only, MVP Option C auth).
 *
 * Scope: submit purchase and refinance referrals as LeadIntake XML. This is a
 * lead-only MVP — no Empower loan is created, and lead status/detail/update
 * behavior is intentionally NOT implemented. The EPS Public referral endpoints
 * and secondary LMD Upsert call are never called from the browser submit path.
 *
 * Auth: LeadIntake credentials come from runtime config and are sent from the
 * browser. These values are browser-visible after deployment and are NOT secrets.
 */
import type { RuntimeConfig } from "./config";
import {
  buildLeadIntakeXmlPayload,
  leadIntakeApimBaseUrl,
  LEAD_INTAKE_APIM_SUBSCRIPTION_HEADER,
  parseLeadIntakeResponse,
} from "./lead-intake";
import {
  LMD_UPSERT_LEAD_PATH,
  type LmdSubmissionResult,
  type LmdUpsertLeadRequest,
} from "./models/lmd";

export const HEADER_CORRELATION_ID = "X-Correlation-Id";
export const HEADER_CLIENT_APP = "X-Client-App";

/**
 * Sanitized taxonomy of submit failure classes. Each value is a stable,
 * non-secret category safe to surface in the UI, in an outward postMessage, and
 * in telemetry-like outputs:
 * - `config`       — the SPA is misconfigured (missing key/base URL); needs support.
 * - `validation`   — LMD rejected the payload (HTTP 400); the user can fix the form.
 * - `unauthorized` — auth/authorization failure (HTTP 401/403); needs support.
 * - `rateLimited`  — too many requests (HTTP 429); retry after a pause.
 * - `server`       — retryable server failure (HTTP 5xx); retry later.
 * - `network`       — network/CORS/unreachable (fetch rejected before a response).
 * - `indeterminate` — lead state is unknown after LeadIntake; do not retry in browser.
 * - `unknown`       — any other/unexpected non-OK outcome.
 */
export type ApiErrorKind =
  | "config"
  | "validation"
  | "unauthorized"
  | "rateLimited"
  | "server"
  | "network"
  | "indeterminate"
  | "unknown";

/**
 * The full set of sanitized submit outcomes, including the success case. Used as
 * the outcome-category vocabulary for telemetry and monitoring.
 */
export type SubmitOutcomeCategory = "success" | ApiErrorKind;

/** Endpoint the browser submit path posts to. Exposed for tests/documentation. */
export { LMD_UPSERT_LEAD_PATH };

export class ReferralApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;

  constructor(kind: ApiErrorKind, message: string, status?: number) {
    super(message);
    this.name = "ReferralApiError";
    this.kind = kind;
    if (status !== undefined) this.status = status;
  }
}

export interface ReferralApiClientOptions {
  fetchFn?: typeof fetch;
  correlationIdFactory?: () => string;
}

/**
 * Generates a browser-safe correlation id for a single submission. Exposed so
 * the submit flow can reuse one id for the LeadIntake request header AND the
 * outward embedder postMessage.
 */
export function createCorrelationId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `tv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const FRIENDLY_MESSAGES: Record<ApiErrorKind, string> = {
  config: "The referral form is not configured correctly. Please contact support.",
  validation: "Some referral details were rejected. Please review the form and try again.",
  unauthorized: "This referral could not be authorized. Please contact your loanDepot administrator.",
  rateLimited: "Too many requests right now. Please wait a moment and try again.",
  server: "loanDepot could not process the referral right now. Please try again later.",
  network: "We could not reach loanDepot. Please check your connection and try again.",
  indeterminate: "We could not confirm the referral status. Please contact support before submitting again.",
  unknown: "Something went wrong while submitting the referral. Please contact support before submitting again.",
};

/**
 * Canonical, already-sanitized friendly message for an error kind. Never
 * includes raw response bodies, underlying exception text, or secrets, so it is
 * safe to surface both in the UI and in an outward postMessage.
 */
export function friendlyReferralMessage(kind: ApiErrorKind): string {
  return FRIENDLY_MESSAGES[kind];
}

/**
 * Whether the borrower can reasonably retry the same submission. Auth and
 * configuration problems require support, everything else is transient or
 * form-fixable.
 */
export function isRetryableKind(kind: ApiErrorKind): boolean {
  return !["unauthorized", "config", "indeterminate", "unknown"].includes(kind);
}

function kindForStatus(status: number): Exclude<ApiErrorKind, "config" | "network"> {
  if (status === 400) return "validation";
  if (status === 401 || status === 403) return "unauthorized";
  if (status === 429) return "rateLimited";
  if (status >= 500) return "server";
  return "unknown";
}

function readLeadGuid(body: Record<string, unknown>): string | undefined {
  if (typeof body.leadGuid === "string" && body.leadGuid.trim() !== "") return body.leadGuid;
  const lead = body.lead;
  if (typeof lead === "object" && lead !== null) {
    const guid = (lead as Record<string, unknown>).leadGuid;
    if (typeof guid === "string" && guid.trim() !== "") return guid;
  }
  return undefined;
}

/**
 * Parses a legacy LMD UpsertLead success body into a sanitized, lead-only result.
 *
 * Lead-only semantics: success does NOT require an Empower loan GUID, loan
 * number, status link, or detail link. A returned lead GUID is captured when
 * present but is optional. Any raw message text is intentionally NOT surfaced.
 */
export function parseUpsertLeadResponse(raw: unknown): LmdSubmissionResult {
  const result: LmdSubmissionResult = {
    status: "Accepted",
    submittedAt: new Date().toISOString(),
  };

  if (typeof raw === "object" && raw !== null) {
    const obj = raw as Record<string, unknown>;
    // Accept either the raw model or a { data } envelope.
    const body =
      typeof obj.data === "object" && obj.data !== null
        ? (obj.data as Record<string, unknown>)
        : obj;

    if (typeof body.status === "string" && body.status.trim() !== "") result.status = body.status;
    if (typeof body.submittedAt === "string" && body.submittedAt.trim() !== "") {
      result.submittedAt = body.submittedAt;
    }
    const leadGuid = readLeadGuid(body);
    if (leadGuid) result.leadGuid = leadGuid;
  }

  return result;
}

function validateSubmitConfig(config: RuntimeConfig): void {
  if (!config.leadIntakeApiBaseUrl || config.leadIntakeApiBaseUrl.trim() === "") {
    throw new ReferralApiError(
      "config",
      "No LeadIntake API base URL is configured. Set 'leadIntakeApiBaseUrl' in the runtime config.",
    );
  }
  if (!config.leadIntakeApiKey || config.leadIntakeApiKey.trim() === "") {
    throw new ReferralApiError(
      "config",
      "No LeadIntake API key is configured. Set 'leadIntakeApiKey' in the runtime config before submitting.",
    );
  }
  if (!config.leadIntakePassword || config.leadIntakePassword.trim() === "") {
    throw new ReferralApiError(
      "config",
      "No LeadIntake password is configured. Set 'leadIntakePassword' in the runtime config before submitting.",
    );
  }
}

async function postLeadIntake(
  request: LmdUpsertLeadRequest,
  config: RuntimeConfig,
  fetchFn: typeof fetch,
  correlationId: string,
): Promise<string> {
  let response: Response;
  try {
    response = await fetchFn(`${leadIntakeApimBaseUrl(config)}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/xml",
        Accept: "application/xml",
        [LEAD_INTAKE_APIM_SUBSCRIPTION_HEADER]: config.leadIntakeApiKey,
        [HEADER_CORRELATION_ID]: correlationId,
        [HEADER_CLIENT_APP]: config.clientAppName,
      },
      body: buildLeadIntakeXmlPayload(request, correlationId, config.leadIntakePassword),
    });
  } catch {
    throw new ReferralApiError("indeterminate", FRIENDLY_MESSAGES.indeterminate);
  }

  if (!response.ok) {
    const kind = kindForStatus(response.status);
    throw new ReferralApiError(kind, FRIENDLY_MESSAGES[kind], response.status);
  }

  let raw: string;
  try {
    raw = await response.text();
  } catch {
    throw new ReferralApiError("indeterminate", FRIENDLY_MESSAGES.indeterminate);
  }

  const parsed = parseLeadIntakeResponse(raw);
  if (parsed.status && /fail|error/i.test(parsed.status)) {
    throw new ReferralApiError("indeterminate", FRIENDLY_MESSAGES.indeterminate);
  }
  if (!parsed.ldGuid || parsed.ldGuid.trim() === "") {
    throw new ReferralApiError("indeterminate", FRIENDLY_MESSAGES.indeterminate);
  }
  return parsed.ldGuid;
}

function requestFingerprint(request: LmdUpsertLeadRequest): string {
  const lead = { ...request.lead };
  delete lead.leadGuid;
  return JSON.stringify({ ...request, lead });
}

async function postLead(
  request: LmdUpsertLeadRequest,
  config: RuntimeConfig,
  options: ReferralApiClientOptions,
  leadGuidProvider: (fingerprint: string) => string | undefined,
  leadGuidRecorder: (fingerprint: string, leadGuid: string) => void,
): Promise<LmdSubmissionResult> {
  validateSubmitConfig(config);
  const fetchFn = options.fetchFn ?? fetch;
  const correlationId = (options.correlationIdFactory ?? createCorrelationId)();
  const fingerprint = requestFingerprint(request);
  const leadGuid =
    leadGuidProvider(fingerprint) ?? (await postLeadIntake(request, config, fetchFn, correlationId));
  leadGuidRecorder(fingerprint, leadGuid);
  return {
    status: "Accepted",
    submittedAt: new Date().toISOString(),
    leadGuid,
  };
}

/**
 * Creates the lead-submission client. Both purchase and refinance submits are
 * mapped to LeadIntake XML by the caller; this client only knows how to post an
 * already-built `LmdUpsertLeadRequest`.
 */
export function createLeadSubmissionClient(
  config: RuntimeConfig,
  options: ReferralApiClientOptions = {},
) {
  let submittedLeadGuid: { fingerprint: string; leadGuid: string } | undefined;
  return {
    submitLead(request: LmdUpsertLeadRequest): Promise<LmdSubmissionResult> {
      return postLead(
        request,
        config,
        options,
        (fingerprint) =>
          submittedLeadGuid?.fingerprint === fingerprint ? submittedLeadGuid.leadGuid : undefined,
        (fingerprint, leadGuid) => {
          submittedLeadGuid = { fingerprint, leadGuid };
        },
      );
    },
  };
}

export type LeadSubmissionClient = ReturnType<typeof createLeadSubmissionClient>;
